import phoneCode from "./phoneCode";

export const REVERSE_GEOCODE_ENDPOINT =
  "https://nominatim.openstreetmap.org/reverse";

export const PAGE_SIZE_OPTIONS = [5, 10, 15, 25, 50, 100] as const;

export const getPageSizeOptions = (totalRecords: number, currentLimit: number): number[] => {
  const minimumOptions = PAGE_SIZE_OPTIONS.slice(0, 2);
  const availableOptions = PAGE_SIZE_OPTIONS.filter(
    (option) => option <= totalRecords
  );

  return Array.from(
    new Set([...minimumOptions, ...availableOptions, currentLimit])
  ).sort((first, second) => first - second);
};

export const capitalizeString = (str: string): string => {
  if (!str) return "";

  return `${str[0].toUpperCase()}${str.slice(1)}`;
};

export const getMarkColor = (mark: number): string => {
  if (mark >= 8) return "green";
  if (mark >= 5) return "goldenrod";
  return "red";
};

export function generatePassword() {
  const length = getRandomInt(8, 10);
  const uppercase = getRandomUppercase();
  const specialChar = getRandomSpecialCharacter();
  const number = getRandomNumber();
  const remainingChars = length - 3;

  const passwordArray = [
    uppercase,
    specialChar,
    number,
    ...generateRandomChars(remainingChars),
  ];

  // Shuffle the password array to randomize the order
  const shuffledPassword = shuffleArray(passwordArray);

  // Convert the array to a string
  const password = shuffledPassword.join("");

  return password;
}

function getRandomInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function getRandomUppercase() {
  const uppercaseChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  return uppercaseChars.charAt(getRandomInt(0, uppercaseChars.length - 1));
}

function getRandomSpecialCharacter() {
  const specialChars = "!@#$%&";
  return specialChars.charAt(getRandomInt(0, specialChars.length - 1));
}

function getRandomNumber() {
  return getRandomInt(0, 9).toString();
}

function generateRandomChars(length: number) {
  const chars = "abcdefghijklmnopqrstuvwxyz";
  return Array.from({ length }, () =>
    chars.charAt(getRandomInt(0, chars.length - 1))
  );
}

function shuffleArray(array: any) {
  // Fisher-Yates shuffle algorithm
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

export const phoneCodeOptions: any = phoneCode.map((item: any) => ({
  value: item.dial_code,
  label: `${item.code} (${item.dial_code})`,
}));

const VALID_IMAGE_PREFIXES = ["http://", "https://", "data:image/", "/", "blob:"];

export const isValidImageUrl = (url?: string): boolean => {
  if (typeof url !== "string") return false;
  const trimmed = url.trim();
  return VALID_IMAGE_PREFIXES.some((prefix) => trimmed.startsWith(prefix));
};

export const getDurationInMonths = (cycle: string): number | undefined => {
  switch (cycle) {
    case "1 Month":
      return 1;
    case "3 Months":
      return 3;
    case "6 Months":
      return 6;
    case "12 Months":
      return 12;
    default:
      const match = cycle ? cycle.match(/\d+/) : null;
      return match ? parseInt(match[0], 10) : undefined;
  }
};

export const SUPERUSER_BASE_ROUTE = "/superuser";
export const SUPERUSER_DASHBOARD_ROUTE = "/superuser/dashboard";

export const getStatusBadgeClass = (status?: string): string => {
  if (!status) return "status-badge-gray";
  const s = status.toUpperCase();
  if (s.includes("APPROV") || s.includes("COMPLET")) return "status-badge-emerald";
  if (s.includes("REJECT") || s.includes("FAIL")) return "status-badge-rose";
  if (s.includes("PENDING") || s.includes("REVIEW")) return "status-badge-amber";
  return "status-badge-blue";
};

export interface CrSummaryFieldConfig {
  label: string;
  key: string;
  isStatus?: boolean;
  isDate?: boolean;
}

export const CR_SUMMARY_FIELDS: CrSummaryFieldConfig[] = [
  { label: "Reference", key: "changeRequestId" },
  { label: "Application", key: "applicationId" },
  { label: "Status", key: "status", isStatus: true },
  { label: "Created At", key: "createdAt", isDate: true },
];

/**
 * Generic query string builder that filters out undefined, null, and empty string values.
 * Supports key aliases (e.g. { ip: 'IP' }) and eliminates duplicate keys.
 */
export const buildQueryString = <T extends object>(
  data?: T,
  keyAliases: Record<string, string> = {}
): string => {
  if (!data) return "";
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined && value !== null && value !== "") {
      const paramKey = keyAliases[key] || key;
      if (!params.has(paramKey)) {
        params.append(paramKey, String(value));
      }
    }
  }

  const query = params.toString();
  return query ? `?${query}` : "";
};

